"use client";

/**
 * `<AnalyzerCard>` — one cell in the SevenAnalyzers grid.
 * Hover state lightens the background and brightens the corner dot.
 */

import { memo, useState } from "react";

interface AnalyzerCardProps {
  k: string;
  name: string;
  desc: string;
  bullets: string[];
  accent: string;
}

function AnalyzerCardImpl({
  k,
  name,
  desc,
  bullets,
  accent,
}: AnalyzerCardProps) {
  const [hover, setHover] = useState(false);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover
          ? "var(--color-surface-2)"
          : "var(--color-surface)",
        padding: "28px 26px",
        height: "100%",
        transition: "background 0.25s",
        cursor: "default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 16,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: accent,
            letterSpacing: "0.14em",
            fontWeight: 600,
          }}
        >
          {k}
        </div>
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: accent,
            opacity: hover ? 1 : 0.5,
            transition: "opacity 0.2s",
          }}
        />
      </div>
      <h3
        style={{
          fontSize: 17,
          fontWeight: 600,
          color: "var(--color-text)",
          letterSpacing: "-0.015em",
          margin: 0,
          marginBottom: 8,
        }}
      >
        {name}
      </h3>
      <p
        style={{
          fontSize: 13,
          color: "var(--color-muted)",
          lineHeight: 1.55,
          margin: 0,
          marginBottom: 18,
        }}
      >
        {desc}
      </p>
      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
          marginTop: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        {bullets.map((b) => (
          <li
            key={b}
            style={{
              fontSize: 12,
              color: "var(--color-dim)",
              lineHeight: 1.5,
              display: "flex",
              gap: 8,
            }}
          >
            <span style={{ color: accent, opacity: 0.7 }}>·</span> {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

export const AnalyzerCard = memo(AnalyzerCardImpl);
